import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PHASE_LABELS, type ProjectPhase } from "@/lib/offer-types";
import { formatNOK } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import type { OfferLineTemplate } from "@/hooks/use-offers";
import type { PreviewLine, PreviewMetadata } from "@/hooks/use-import-preview";
import { TemplatePicker } from "../TemplatePicker";

const UNITS: Array<{ value: string; label: string }> = [
  { value: "t", label: "t (timer)" },
  { value: "d", label: "d (dager)" },
  { value: "stk", label: "stk" },
  { value: "fast", label: "fast" },
];

interface ImportPreviewTableProps {
  lines: PreviewLine[];
  templates: OfferLineTemplate[];
  metadata: PreviewMetadata | null;
  onUpdate: (id: string, patch: Partial<PreviewLine>) => void;
  onRemove: (id: string) => void;
}

/**
 * Tabell-delen av ImportBudgetDialog preview-steget. Lar PL korrigere
 * beskrivelse, fase (når umatchet), antall, enhet og beløp per linje, og
 * fjerne linjer før import. Footer viser linjesum + overhead + rabatt +
 * tilbudssum.
 */
export function ImportPreviewTable({
  lines,
  templates,
  metadata,
  onUpdate,
  onRemove,
}: ImportPreviewTableProps) {
  const linesTotal = lines.reduce((s, l) => s + l.amount, 0);
  const overheadAmount = metadata
    ? Math.round(linesTotal * (metadata.overhead_pct / 100))
    : 0;
  const computedDiscount = metadata
    ? metadata.discount_amount > 0
      ? metadata.discount_amount
      : Math.round((linesTotal + overheadAmount) * (metadata.discount_pct / 100))
    : 0;
  const totalAmount = linesTotal + overheadAmount - computedDiscount;

  return (
    <table className="w-full text-sm table-fixed">
      <colgroup>
        <col />
        <col className="w-[140px]" />
        <col className="w-[68px]" />
        <col className="w-[64px]" />
        <col className="w-[100px]" />
        <col className="w-[32px]" />
      </colgroup>
      <thead>
        <tr className="border-b text-[11px] uppercase tracking-wider text-muted-foreground">
          <th className="text-left py-2 px-2 font-medium">Beskrivelse</th>
          <th className="text-left py-2 px-2 font-medium">Fase</th>
          <th className="text-right py-2 px-2 font-medium">Antall</th>
          <th className="text-left py-2 px-2 font-medium">Enhet</th>
          <th className="text-right py-2 px-2 font-medium">Beløp</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => {
          const matched = line.template_id !== null;
          return (
            <tr key={line.id} className="border-b border-border/40 align-top">
              <td className="py-1.5 px-2">
                <Input
                  value={line.description}
                  className="h-8"
                  onChange={(e) => onUpdate(line.id, { description: e.target.value })}
                />
                <TemplatePicker
                  templates={templates}
                  currentId={line.template_id}
                  currentName={line.template_name}
                  onSelect={(t) => {
                    if (t) {
                      onUpdate(line.id, {
                        template_id: t.id,
                        template_name: t.name,
                        phase: t.phase as ProjectPhase,
                        cost_type:
                          t.cost_type === "external" ? "external" : "internal",
                        unit: t.default_unit ?? line.unit,
                      });
                    } else {
                      onUpdate(line.id, {
                        template_id: null,
                        template_name: null,
                      });
                    }
                  }}
                />
              </td>
              <td className="py-1.5 px-2">
                {matched ? (
                  // Mal styrer fasen — vises som tekst, ikke editerbart
                  <span className="text-xs text-muted-foreground block py-1.5 truncate">
                    {PHASE_LABELS[line.phase]}
                  </span>
                ) : (
                  <Select
                    value={line.phase}
                    onValueChange={(v) =>
                      onUpdate(line.id, { phase: v as ProjectPhase })
                    }
                  >
                    <SelectTrigger className="h-8 px-2 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(PHASE_LABELS) as ProjectPhase[]).map((p) => (
                        <SelectItem key={p} value={p}>
                          {PHASE_LABELS[p]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </td>
              <td className="py-1.5 px-2">
                <Input
                  type="number"
                  step="0.25"
                  className="h-8 px-2 text-right tabular-nums"
                  value={line.quantity || ""}
                  onChange={(e) =>
                    onUpdate(line.id, { quantity: Number(e.target.value) })
                  }
                />
              </td>
              <td className="py-1.5 px-2">
                <Select
                  value={line.unit}
                  onValueChange={(v) => onUpdate(line.id, { unit: v })}
                >
                  <SelectTrigger className="h-8 px-2 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => (
                      <SelectItem key={u.value} value={u.value}>
                        {u.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </td>
              <td className="py-1.5 px-2">
                <Input
                  type="number"
                  className="h-8 text-right tabular-nums"
                  value={line.amount || ""}
                  onChange={(e) =>
                    onUpdate(line.id, { amount: Number(e.target.value) })
                  }
                />
              </td>
              <td className="py-1.5 px-2 text-right">
                <button
                  onClick={() => onRemove(line.id)}
                  className="text-muted-foreground hover:text-destructive"
                  title="Fjern linje"
                >
                  <SrIcon name="trash2" className="w-3.5 h-3.5" />
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr className="border-t">
          <td colSpan={4} className="py-1 px-2 text-right text-[11px] text-muted-foreground">
            Sum linjer
          </td>
          <td className="py-1 px-2 text-right tabular-nums text-[11px] text-muted-foreground">
            {formatNOK(linesTotal)}
          </td>
          <td />
        </tr>
        {metadata && metadata.overhead_pct > 0 && (
          <tr>
            <td colSpan={4} className="py-1 px-2 text-right text-[11px] text-muted-foreground">
              Overhead ({metadata.overhead_pct}%)
            </td>
            <td className="py-1 px-2 text-right tabular-nums text-[11px] text-muted-foreground">
              +{formatNOK(overheadAmount)}
            </td>
            <td />
          </tr>
        )}
        {metadata && computedDiscount > 0 && (
          <tr>
            <td colSpan={4} className="py-1 px-2 text-right text-[11px] text-muted-foreground">
              Rabatt
              {metadata.discount_pct > 0 ? ` (${metadata.discount_pct}%)` : ""}
            </td>
            <td className="py-1 px-2 text-right tabular-nums text-[11px] text-muted-foreground">
              −{formatNOK(computedDiscount)}
            </td>
            <td />
          </tr>
        )}
        <tr className="border-t-2">
          <td colSpan={4} className="py-2 px-2 text-right text-xs text-muted-foreground">
            Tilbudssum
          </td>
          <td className="py-2 px-2 text-right tabular-nums font-semibold">
            {formatNOK(totalAmount)}
          </td>
          <td />
        </tr>
      </tfoot>
    </table>
  );
}
